/**
 * useConfigQueries — TanStack Query hooks for tenant configuration pages.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useQueryScope } from '../utils/queryScope';
import {
  usePersonnelRolesQuery,
  invalidatePersonnelRoles,
} from './usePersonnelRolesQueries';

const ROOT = 'config';

const sharedQueryDefaults = {
  retry: 0,
};

function useScope() {
  return useQueryScope();
}

export function useDemoStatus({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'demo-status'],
    queryFn: async () => {
      const { data } = await api.get('/demo/status');
      return data;
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function usePersonnelRolesCatalog({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'personnel-roles-catalog'],
    queryFn: async () => {
      const { data } = await api.get('/personnel-roles?catalog=1');
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

/**
 * All personnel roles (e.g. daily journal add-personnel dropdown).
 * Shared cache — see `usePersonnelRolesQueries`.
 */
export function usePersonnelRolesList(options = {}) {
  return usePersonnelRolesQuery(options);
}

export function useClientsList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'clients'],
    queryFn: async () => {
      const { data } = await api.get('/clients');
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function usePersonnelDepartmentsList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'personnel-departments'],
    queryFn: async () => {
      const { data } = await api.get('/personnel-departments');
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useEquipmentCategoriesList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'equipment-categories'],
    queryFn: async () => {
      const { data } = await api.get('/equipment-categories');
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useConfigInvalidator() {
  const queryClient = useQueryClient();
  const scope = useScope();

  return useMemo(() => {
    const root = [ROOT, scope];
    const inv = (key) => queryClient.invalidateQueries({ queryKey: key });

    return {
      demoStatus: () => inv([...root, 'demo-status']),
      // Job positions: always bust every derived cache (catalog view, candidate /
      // personnel / timesheet / budget dropdowns), never just the local key.
      personnelRolesCatalog: () => invalidatePersonnelRoles(queryClient),
      personnelRolesList: () => invalidatePersonnelRoles(queryClient),
      personnelRoles: () => invalidatePersonnelRoles(queryClient),
      clients: () => inv([...root, 'clients']),
      personnelDepartments: () => inv([...root, 'personnel-departments']),
      equipmentCategories: () => inv([...root, 'equipment-categories']),
      all: () => inv(root),
    };
  }, [queryClient, scope]);
}
